/**
 * The work-order side's strings (piece 29): agreements and recurring billing,
 * the coverage question, emergency intake and its text, the dispatch board and
 * the tier editor. Spread into the one lookup in `strings.ts`, so `t()` stays
 * the only way a screen reads text. 🔨 This file imports nothing, because the
 * lookup imports it.
 */
export const WORK_STRINGS = {
  'jobs.generateScheduledInvoices.label': 'Raise recurring invoices',
  'jobs.generateScheduledInvoices.purpose': 'Raises one Draft Invoice for each active Agreement whose next billing date has arrived. It never sends one.',
  'jobs.sendVisitReminders.label': 'Send visit reminders',
  'jobs.sendVisitReminders.purpose': 'Sends the customer’s Reminder for each visit booked tomorrow, where the customer or the Deal has it switched on. A visit is reminded once.',
  'jobs.recheckVisitCrews.label': 'Recheck visit crews',
  'jobs.recheckVisitCrews.purpose': 'Checks every worker on each visit sent for today against the site’s requirements, the sub-skills and the certifications again. A worker who now falls short raises a To-Do on whoever booked the visit, and nothing is held.',
  'work.crewRecheck.todo': 'Check {worker} on {visit} of {job} today',
  'work.crewRecheck.todoDetail': '{worker} is booked on {visit} of {job} today and now falls short: {lapses}. Swap the worker or cancel the visit, or let them go. The To-Do closes once the worker is off the visit, the visit is no longer Dispatched, or the check passes again.',
  // The notes an assignment writes on the line's Asset and on the Deal (piece 29).
  'workOrder.assignment.assetNote': 'Work Order visit booked:',
  'workOrder.assignment.dealNote': 'Field visit booked:',
  // The Work Order page's field side (piece 29).
  'workOrder.line.notNeeded': 'Not Needed',
  'workOrder.line.statusComplete': 'Complete',
  'workOrder.line.statusNotNeeded': 'Not needed: rolls to no follow-up and uses no part',
  'workOrder.line.asset': 'Asset',
  'workOrder.line.noAsset': 'No asset named',
  'workOrder.line.dueDate': 'Due date',
  'workOrder.line.noDueDate': 'No due date',
  'workOrder.line.work': 'Work',
  'workOrder.budget.budgeted': 'Budget',
  'workOrder.budget.actual': 'Actual',
  'workOrder.budget.remaining': 'Remaining',
  'workOrder.budget.overrun': 'Over budget by',
  'workOrder.checklist.show': 'Checklists',
  'workOrder.checklist.hide': 'Hide Checklists',
  'workOrder.checklist.none': 'This line’s Asset offers no checklist.',
  'workOrder.checklist.attach': 'Add to the Job',
  'workOrder.checklist.attached': 'On the job',
  'workOrder.checklist.workersMet': 'Workers the checklist asks for:',
  'workOrder.checklist.workersShort': 'Fewer workers than the checklist asks for:',
  'workOrder.gate.blocked': 'Blocked:',
  'workOrder.onSite.label': 'On site',
  'reports.projectPerformance.onSite': 'On site',
  // The Agreement and the coverage question (piece 29).
  'agreement.coverage.todo': 'Is there work under a standing contract?',
  'agreement.coverage.clash': 'Another active Agreement already covers one of these sites or asset nodes, or a node above or below one; coverage always resolves to a single Agreement.',
  'agreement.coverage.expansionSuffix': 'expansion',
  'agreement.type.retainer': 'Retainer',
  'agreement.type.serviceContract': 'Service contract',
  'agreement.type.masterProject': 'Master project',
  'agreement.cadence.monthly': 'Monthly',
  'agreement.cadence.quarterly': 'Quarterly',
  'agreement.cadence.annually': 'Annually',
  'agreement.subtitle': 'Standing contracts',
  'agreement.list.title': 'Agreements',
  'agreement.list.hint': 'The standing contracts won Deals became: what each covers, how it bills, and the work done under it. Winning a Deal asks whether one is needed, and New Agreement makes one from a won Deal that has none.',
  'agreement.list.empty': 'No Agreement yet. Winning a Deal asks whether one is needed.',
  'agreement.openEnded': 'open-ended',
  'agreement.fromDeal': 'from',
  'agreement.sourceDeal': 'Won on the Deal',
  'agreement.active': 'Active today',
  'agreement.inactive': 'Not active today',
  'agreement.back': 'Back',
  'agreement.facts': 'The contract',
  'agreement.dates': 'Runs',
  'agreement.renewal': 'Renewal reminder',
  'agreement.status': 'Status',
  'agreement.rate': 'Labor rate',
  'agreement.levelRate': 'Each worker’s own level rate',
  'agreement.premiums': 'Premiums',
  'agreement.priorityPremium': 'priority premium applies',
  'agreement.noPriorityPremium': 'no priority premium',
  'agreement.afterHoursPremium': 'after-hours premium applies',
  'agreement.noAfterHoursPremium': 'no after-hours premium',
  'agreement.covers': 'Covers:',
  'agreement.coversNothing': 'nothing named',
  'agreement.schedule': 'Billing',
  'agreement.nextBilling': 'Next bills on',
  'agreement.nothingToBill': 'nothing more to bill',
  'agreement.noSchedule': 'No billing schedule: this contract bills nothing on a timer.',
  'agreement.onDay': 'on day',
  'agreement.inForce': 'in force',
  'agreement.cadence': 'How often',
  'agreement.amount': 'Amount',
  'agreement.day': 'Billing day',
  'agreement.effectiveFrom': 'From',
  'agreement.saveChange': 'Save the New Price',
  'agreement.changeNeeds': 'Give the amount, a billing day of 28 or less and the date it takes effect.',
  'agreement.startBilling': 'Start Billing',
  'agreement.changePrice': 'Change the Price',
  'agreement.dayCap': 'A price change closes the row in force and opens the next; nothing is edited in place. The billing day is capped at',
  'agreement.invoices': 'Recurring invoices',
  'agreement.noInvoices': 'Nothing raised yet.',
  'agreement.draftUnnumbered': 'Draft, numbered when sent',
  'agreement.work': 'Work under this contract',
  'agreement.noWork': 'No Work Order or Deal names this Agreement yet.',
  'agreement.type': 'Type',
  'agreement.start': 'Starts',
  'agreement.end': 'Ends',
  'agreement.coveredSite': 'Covered site',
  'agreement.coverage.title': 'Coverage Question',
  'agreement.coverage.question': 'Is there something covering work nobody has identified yet?',
  'agreement.coverage.questionHint': 'A retainer, a service contract or a master project covers work before anyone knows what it is. A job sold again as priced work is a new Deal instead.',
  'agreement.coverage.yes': 'Yes, a Standing Contract',
  'agreement.coverage.no': 'No',
  'agreement.coverage.agreementTitle': 'The Agreement this Deal becomes',
  'agreement.coverage.masterProject': 'A master project is billed on progress, so it needs no billing schedule.',
  'agreement.coverage.create': 'Create the Agreement',
  'agreement.coverage.needsStart': 'Give the date the contract starts, and a billing day of 28 or less.',
  'agreement.coverage.soldAgainQuestion': 'Was more work sold off this job as identified, priced work?',
  'agreement.coverage.soldAgainHint': 'Yes opens a new Deal that carries this one. If neither applies, say so and the question closes.',
  'agreement.coverage.expansionName': 'Name of the new Deal',
  'agreement.coverage.createExpansion': 'Yes, Open the New Deal',
  'agreement.coverage.neither': 'Neither Applies',
  'agreement.coverage.recorded': 'Answered: neither applies.',
  'agreement.coverage.alreadyAnswered': 'This question has been answered.',
  // The Agreement's terms edited on its page, and New Agreement on the list (piece 29; decision 3900).
  'agreement.edit': 'Edit',
  'agreement.edit.title': 'Edit the Agreement',
  'agreement.edit.save': 'Save Changes',
  'agreement.edit.needs': 'Give the date the contract starts, and the cap in minutes where travel is capped.',
  'agreement.coveredSites': 'Covered sites',
  'agreement.coveredNodes': 'Covered asset nodes',
  'agreement.noSites': 'The customer has no current site to cover.',
  'agreement.rateOverride': 'Agreed labor rate',
  'agreement.rateHint': 'Blank bills each worker at their own level’s rate.',
  'agreement.waivePriority': 'Waive the priority premium',
  'agreement.waiveAfterHours': 'Waive the after-hours premium',
  'agreement.travel': 'Travel',
  'agreement.travel.billed': 'Billed as worked',
  'agreement.travel.covered': 'Covered by the contract',
  'agreement.travel.capped': 'Capped per visit',
  'agreement.travel.cappedAt': 'Capped at {minutes} minutes a visit',
  'agreement.travelCap': 'Travel cap in minutes',
  'agreement.includedHours': 'Included hours per period',
  'agreement.includedHint': 'The hours each billing period includes at no charge. Blank includes none.',
  'agreement.includedFact': 'Included hours',
  'agreement.included.used': '{used} used of {allowance} this period, {remaining} remaining',
  'agreement.included.line': 'Hours included by the {type} Agreement from {from}, for {start} to {end}',
  'agreement.renewal.todo': 'Renew the Agreement with {company} by {date}',
  'agreement.renewal.todoDetail': 'The Agreement renews on {date}. Agree the next term with the customer and move the renewal date, or set the end date if it is not renewing; either closes this To-Do.',
  'agreement.financeCharge': 'Finance charge applies',
  'agreement.financeChargeFact': 'Finance charge',
  'agreement.financeCharge.applies': 'applies to late invoices',
  'agreement.financeCharge.none': 'none',
  'agreement.new': 'New Agreement',
  'agreement.new.title': 'New Agreement',
  'agreement.new.deal': 'Won Deal',
  'agreement.new.pickDeal': 'Choose the won Deal',
  'agreement.new.dealHint': 'A won Deal no Agreement has been made from. The customer is the Deal’s.',
  'agreement.new.noDeals': 'Every won Deal already has its Agreement.',
  'agreement.new.customer': 'Customer:',
  'agreement.new.closesQuestion': 'This Deal’s coverage question is still open; creating the Agreement answers it Yes.',
  'agreement.new.needs': 'Choose the won Deal and give the date the contract starts.',
  'agreement.new.dealTaken': 'This Deal already has its Agreement; one won Deal makes one Agreement.',
  // Emergency intake and the customer's text confirmation (piece 29).
  'emergency.title': 'Emergency Call',
  'emergency.hint': 'Confirming the call opens a Won Deal marked Emergency and a Work Order that dispatches at once. Its approval, where a rule asks for one, is reviewed after, in the ordinary Pending Approvals queue.',
  'emergency.moved': 'Emergencies are now booked from New Deal: choose Service and turn Emergency on. This page stays until that form has been reviewed.',
  'emergency.fault': 'What the caller reported',
  'emergency.start': 'Visit starts (blank is now)',
  'emergency.technician': 'Technician',
  'emergency.technicianLater': 'Choose on the dispatch board',
  'emergency.confirm': 'Confirm Emergency',
  'emergency.needs': 'Choose the customer and the site, and say what the caller reported.',
  'emergency.confirmation.title': 'Customer confirmation by text',
  'emergency.confirmation.hint': 'The job is already out. A text asks the customer to authorize a diagnosis visit at the rates it names; no reply holds nothing.',
  'emergency.confirmation.confirmed': 'The customer said yes by text.',
  'emergency.confirmation.contact': 'Who to text',
  'emergency.confirmation.noNumber': 'no mobile number',
  'emergency.confirmation.to': 'To:',
  'emergency.confirmation.send': 'Send the Text',
  'emergency.confirmation.sent': 'Sent to',
  'emergency.confirmation.reply': 'Reply from',
  'emergency.confirmation.recordReply': 'File a Reply',
  'emergency.confirmation.fromNumber': 'From number',
  'emergency.confirmation.replyText': 'What they sent',
  'emergency.confirmation.file': 'File It',
  'emergency.confirmation.fileNeeds': 'Give the number it came from and what it said.',
  // The priority override and the reschedule warning (piece 29).
  'workOrder.priority.override': 'Override Priority',
  'workOrder.priority.current': 'Derived from notice:',
  'workOrder.priority.tier': 'Priority tier',
  'workOrder.priority.reason': 'Why the derived tier is wrong',
  'workOrder.reschedule.warning': 'This move changes the priority tier and the rate:',
  'workOrder.reschedule.confirm': 'Confirm the new date',
  'workOrder.reschedule.open': 'Reschedule',
  // Decision 2813: the reason a Void writes on each visit it cancels, which the workers' canceled notice reads.
  'workOrder.visits.voided': 'The job was voided',
  // The tier editor, the emergency windows and the field reports (piece 29).
  'tiers.title': 'Priority Tiers',
  'tiers.hint': 'How urgent a job is, judged by how much notice the customer gave, and what each level adds to the price. A change takes effect from the day you choose, and a job keeps the prices its level had on its own day.',
  'tiers.none': 'No priority tier yet. Work Orders cannot be raised until one exists.',
  'tiers.under': 'Up to',
  'tiers.hours': 'hours',
  'tiers.minutes': 'minutes',
  'tiers.response': 'response target',
  'tiers.trip': 'Trip charge',
  'tiers.minimum': 'billing minimum from',
  'tiers.from': 'in force from',
  'tiers.notice': 'Notice up to (hours)',
  'tiers.modifier': 'Premium (1 is none)',
  'tiers.responseMinutes': 'Response target (minutes)',
  'tiers.effectiveFrom': 'Takes effect',
  'tiers.save': 'Close This Row and Open the Next',
  'tiers.needsDate': 'Give the day the change takes effect.',
  'tiers.change': 'Change',
  'emergencyApprovals.title': 'Emergency Approvals',
  'emergencyApprovals.hint': 'An emergency dispatches before its approval and the approval is reviewed after. Past this window an unreviewed one reaches the aged list. A customer’s reply matches a text only within the reply window.',
  'emergencyApprovals.window': 'Hours before an unreviewed emergency approval is late',
  'emergencyApprovals.replyWindow': 'Hours after a text that a reply still matches it',
  // Settings › Service › Schedule Notices (piece 29; decision 2250).
  'agreementRenewals.title': 'Agreement Renewals',
  'agreementRenewals.hint': 'How many days before an Agreement’s renewal date its source Deal’s owner gets a To-Do to renew it. No email is sent.',
  'agreementRenewals.days': 'Days before the renewal date',
  // Decisions 1640 and 4001: the on-call escalation's minutes, a section of Dispatch Rules.
  'onCallSettings.title': 'On-Call',
  'onCallSettings.hint': 'How many minutes an after-hours emergency alert waits for the on-call primary to acknowledge it before the day’s backup is alerted.',
  'onCallSettings.minutes': 'Minutes before the backup is alerted',
  'scheduleNotices.title': 'Schedule Notices',
  'scheduleNotices.hint': 'A worker is told of a dispatch or a move once the visit has gone this many minutes without another change, so a schedule being worked sends one notice naming where the visit ended up. A visit moved back where it started sends none. A cancel is told at once.',
  'scheduleNotices.minutes': 'Minutes of quiet before the workers are told',
  'fieldReports.title': 'Field Reports',
  'fieldReports.link': 'Field reports',
  'fieldReports.none': 'Nothing to show.',
  'fieldReports.aged': 'Emergency approvals past their window',
  'fieldReports.agedHint': 'Dispatched before approval and still unreviewed after',
  'fieldReports.confirmations': 'Text confirmations by dispatcher',
  'fieldReports.confirmationsHint': 'A dispatcher whose count climbs may be using the emergency path to skip approval.',
  'fieldReports.response': 'Response against target',
  'fieldReports.responseHint': 'Measured from when the job was raised to when it was dispatched, never to completion.',
  'fieldReports.metTarget': 'met the target of',
  'fieldReports.average': 'average',
  'fieldReports.overrides': 'Priority overrides',
  'fieldReports.unmatched': 'Replies that matched no text',
  'fieldReports.unmatchedHint': 'These authorized nothing. Read each and deal with it by hand.',
  // The dispatch board (piece 29).
  'work.subtitle': 'Operations',
  'work.dispatch.title': 'Dispatch Board',
  'work.dispatch.hint': 'Jobs waiting to go out, highest priority first. Pick one to see who can get there fastest and is qualified: free in the job’s window, then qualified, then nearest. Assign books the visit and sends it, or drag a job onto a worker’s day at the hour it starts.',
  // Decision 5570: Unscheduled and Jobs to Dispatch are one list, Unscheduled, on the board and the Schedule.
  'work.dispatch.jobs': 'Unscheduled',
  'work.dispatch.noJobs': 'Every job waiting to go out has a visit with a crew on it.',
  'work.dispatch.candidates': 'Who can go',
  'work.dispatch.pick': 'Pick a job to see who can go.',
  'work.dispatch.line': 'Line',
  'work.dispatch.noLines': 'This job has no labor line yet; add one on the Work Order first.',
  'work.dispatch.nobody': 'Nobody qualified is free in the job’s window.',
  'work.dispatch.unbooked': 'not booked',
  'work.dispatch.noLevel': 'no skill level',
  'work.dispatch.noDrive': 'drive time not known',
  'work.dispatch.minutes': 'min drive',
  'work.dispatch.assign': 'Assign',
  // Decision 4340: Assign and the drop book the visit and send it, so the notice names the visit and when it starts.
  'work.dispatch.assigned': 'is added to {visit} · {when}, and the visit is sent to them.',
  'work.dispatch.assignedBooked': 'is booked on {visit} · {when}, and the visit is sent to them.',
  'work.dispatch.assignedLater': 'is booked on {visit} · {when}, the next free hour on their day, and the visit is sent to them.',
  // Decision 5570: each Unscheduled card says what it waits on, a visit or a crew on its visit.
  'work.dispatch.waitsNoVisit': 'No visit booked yet',
  'work.dispatch.waitsNoCrew': 'Nobody on the visit yet',
  'work.dispatch.waitsNotSent': 'Not sent to {names}',
  'work.dispatch.waitsSent': 'Sent to {names}',
  'work.dispatch.waitsNobody': 'nobody',
  // Decision 3070: a busy worker stays listed with the clash named; since decision 4340 their Assign books the next free hour.
  'work.dispatch.busy': 'Busy in the job’s window: {event}',
  'work.dispatch.strip': 'The day of {name}',
  'work.dispatch.blockedFold': 'Blocked · {count}',
  'work.dispatch.gatedReason': '{name} cannot go on this job: the reason is under the name.',
  'work.dispatch.pastTitle': 'Assign Below the Line’s Level',
  'work.dispatch.pastHint': 'Say why they go anyway. The reason is kept on the job’s History.',
  'work.dispatch.pastWhy': 'Why they go anyway',
  'work.dispatch.startsSoon': 'Starts Soon',
  'work.dispatch.startsSoonLabel': 'Starts soon, inside the High priority notice window, with no visit booked',
  'work.dispatch.askToMove': 'Ask to Move',
  'work.dispatch.asked': 'Asked whether {visit} can move: {people} told. Nothing has moved yet.',
  'work.dispatch.askedInPlace': '{name}, in place of {other}',
  'work.dispatch.proposeTime': 'Propose a New Time',
  'work.dispatch.proposeReason': 'Proposed from the Dispatch Board: {name} is free then.',
  'work.dispatch.proposeNone': 'Nobody qualified is free in a window between {from} and {to}.',
  'work.dispatch.proposeNoVisit': 'This job has no visit still to come to move.',
  'work.dispatch.proposeSaved': 'The visit moved to the proposed time.',
  // The on-call roster (piece 29; T390; decision 4000), under Work beside the board.
  'work.onCall.title': 'On-Call Roster',
  'work.onCall.hint': 'Who answers after hours: a primary and a backup for each day, in the organization’s time zone. An after-hours emergency alerts the day’s primary, then the day’s backup if the primary has not acknowledged it in time. The roster alerts a person and dispatches nobody.',
  'work.onCall.settingsLink': 'How long the backup waits is set on Dispatch Rules.',
  'work.onCall.weekOf': 'Week of {day}',
  'work.onCall.previous': 'Previous Week',
  'work.onCall.thisWeek': 'This Week',
  'work.onCall.next': 'Next Week',
  'work.onCall.today': 'Today',
  'work.onCall.primary': 'Primary',
  'work.onCall.backup': 'Backup',
  'work.onCall.nobody': 'Nobody',
  'work.onCall.gap': 'Gap',
  'work.onCall.gapLabel': 'A gap: this day has no primary or no backup.',
  'work.onCall.save': 'Save',
  'work.onCall.saveReason': 'Change a place on this day first.',
  'work.onCall.saved': '{day} is saved.',
  'work.onCall.swapDay': 'Swap Primary and Backup',
  'work.onCall.swapDayReason': 'Both places need somebody in them, and the day saved, before they trade.',
  'work.onCall.swapTitle': 'Swap Two Places',
  'work.onCall.swapHint': 'Trade one person’s day with another person’s. Both days change in one save, and the history names both sides.',
  'work.onCall.swapFirst': 'First place',
  'work.onCall.swapSecond': 'Second place',
  'work.onCall.pickPlace': 'Pick a place',
  'work.onCall.place': '{day} · {role} · {name}',
  'work.onCall.swap': 'Swap',
  'work.onCall.swapReason': 'Pick two different places, each with somebody in it.',
  'work.onCall.swapped': 'The two places are swapped.',
  // Decision 4160 (T2406): the standing week, and a day it fills.
  'work.onCall.fromStanding': 'From the standing week',
  'work.onCall.standingTitle': 'Standing Week',
  'work.onCall.standingHint': 'The two people any day takes when it has none of its own. A day’s own save overrides the standing week for that day alone, and clearing both of a day’s places hands it back.',
  'work.onCall.standingDay': 'The standing {weekday}',
  'work.onCall.standingGapLabel': 'A gap: this weekday has no primary or no backup.',
  'work.onCall.standingSaveReason': 'Change a place on this weekday first.',
  'work.dispatch.unacknowledged': 'Unacknowledged',
  'work.dispatch.unacknowledgedLabel': 'An after-hours emergency nobody on call has acknowledged',
  'workOrder.onCall.title': 'On-Call',
  'workOrder.onCall.nobody': 'No one is on call today. Nobody was alerted about this emergency.',
  'workOrder.onCall.unacknowledged': 'Nobody on call has acknowledged this emergency yet.',
  'workOrder.onCall.acknowledgedNotice': 'Acknowledged. This alert is answered.',
  'workOrder.onCall.alertWords.lead': 'After-hours emergency',
  'workOrder.onCall.alertWords.leadBackup': 'After-hours emergency, the primary has not answered',
  'workOrder.onCall.alertWords.atSite': '{customer} at {site}',
  'workOrder.onCall.alertWords.fault': ': {fault}',
  'workOrder.onCall.alertWords.summary': '{lead}. {number} for {where}{fault}.',
  'workOrder.onCall.alertWords.text': '{summary} Tap to acknowledge: {link}',
  'workOrder.onCall.alerted': '{name}, {role}, alerted {at}',
  'workOrder.onCall.primary': 'primary',
  'workOrder.onCall.backup': 'backup',
  'workOrder.onCall.inAppOnly': 'in the app only, no mobile number on file',
  'workOrder.onCall.text': 'text {status}',
  'workOrder.onCall.acknowledged': 'acknowledged {at}',
  'workOrder.onCall.notAcknowledged': 'not acknowledged',
  'workOrder.onCall.acknowledge': 'Acknowledge',
  'workOrder.onSite.labour': 'labor booked',
  'workOrder.retrospective.banner': 'This emergency was dispatched before its approval. The approval is still owed and waits in Pending Approvals as a retrospective review.',
  'workOrder.retrospective.open': 'Open Pending Approvals',
  'workOrder.priorityTier.missing': 'No priority tier is in force. Add one under Settings › Service › Priority Tiers.',
  'workOrder.priorityTier.open': 'Open Priority Tiers',
  'workOrder.form.noDividers': 'A Work Order’s lines keep no groups; take the divider out and save again.',
  'workOrder.form.linesHint': 'Each line is a piece of labor. Workers, due dates and completion are set on the saved job.',
  // Decision 2611 (T1170): Close means billed or written off.
  'jobClose.unbilled.hours': '{hours} of {person} on {line}',
  'jobClose.unbilled.part': '{quantity} {unit} of {part}',
  'jobClose.unbilled.trip': 'the job’s round trip',
  'jobClose.unbilled.tripOf': 'the round trip of {visit}',
  'jobClose.refusal.unbilled': 'The job is not closed, because these are not on an invoice yet: {unbilled}. Raise an Invoice from the job, or press Close Without Billing and say why it bills nothing.',
  'jobClose.refusal.draft': 'The job is not closed: an invoice from it is still a Draft. Issue that invoice, or delete it, then close the job.',
  'jobClose.refusal.reason': 'Say why the job bills nothing before it closes without billing.',
  'jobClose.owes': 'Not invoiced yet: {unbilled}.',
  'jobClose.title': 'Close Without Billing',
  'jobClose.reason': 'Why the job bills nothing',
  'jobClose.reasonHint': 'Shown on the job and in its History, with who closed it and when. Whatever the job has not invoiced by now bills nothing.',
  'jobClose.confirm': 'Close Without Billing',
  'jobClose.closedWithout': 'Closed without billing by {person} on {at}: {reason}',
} as const;
